import './index.scss';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { unflatten } from 'flat';
import { toast } from 'react-semantic-toasts';
import { Form, Button, Dimmer, Loader } from 'semantic-ui/semantic-ui-imports';
import { getAggregateDetails } from 'services/aggregate';
import axios from '../../services/request';
import { useData } from '../../contexts/data';
import { formatAggregateCalcData } from '../../services/aggregateUtils';
import { AggregateCategory, PowerSupplyOptions } from '../../Types/Aggregates';
import { DefaultAggregateCategories } from '../../components/ProductTable';
import AirflowInputs from '../../components/AirflowInputs';
import AggregateTableRow from '../../components/AggregateTableRow';
import AggregateExpertSelections from '../../components/AggregateExpertSelections';
import { useAuth } from '../../contexts/auth';
import { PromptToSignInContext } from '../../contexts/promptToSignIn';

type ConfigurePagePageProps = {
  categories?: Array<AggregateCategory>;
};

const ConfigurePage = ({
  categories = DefaultAggregateCategories,
}: ConfigurePagePageProps) => {
  const { productId, isExpertPage } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isExpert, user, isAuthenticated } = useAuth();
  const {
    activeAggregate,
    activeUserInputs,
    setActiveUserInputs,
    setActiveAggregate,
  } = useData();
  const [inputs, setInputs] = React.useState(activeUserInputs);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const { promptToSignIn } = React.useContext(PromptToSignInContext);

  // Load aggregate details only (no calculation) - for initial page load
  const loadAggregateDetails = async (aggregateId: string) => {
    setIsLoading(true);
    try {
      const details = await getAggregateDetails(aggregateId);
      if (details) {
        // Merge with existing system_data from localStorage if available
        const existingSystemData = activeAggregate?.system_data;
        const newData = existingSystemData
          ? { ...details, system_data: existingSystemData }
          : details;
        setActiveAggregate(newData);

        // Set power_supply if not already in inputs
        if (
          !activeUserInputs?.power_supply &&
          details.power_supply &&
          details.power_supply !== 'BOTH'
        ) {
          setInputs({ ...inputs, power_supply: details.power_supply });
          setActiveUserInputs({
            ...activeUserInputs,
            power_supply: details.power_supply,
          });
        }
      }
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }
    setIsLoading(false);
  };

  // Calculate aggregate data (PUT request) - for explicit calculate action
  const getAggregateData = async (aggregateId: string) => {
    setIsLoading(true);

    if (!isAuthenticated) {
      setIsLoading(false);
      promptToSignIn();
      return;
    }

    if (user && !isExpert) {
      setIsLoading(false);
      // Temporarily only allow expert users to use page
      return;
    }

    try {
      const newParams = { ...activeUserInputs, ...inputs };
      const params = formatAggregateCalcData(newParams);
      const parsedParams = unflatten(params);
      const { data, status } = await axios.put(
        `/aggregates/${aggregateId}/`,
        parsedParams
      );
      if (status === 200 && data) {
        // get additional aggregate data
        let newData = data;
        const a = await getAggregateDetails(aggregateId);
        if (a) {
          newData = { ...data, ...a };
        }

        setActiveAggregate(newData);

        // set power_supply only if not already set in inputs
        if (!newParams.power_supply) {
          if (newData?.power_supply && newData.power_supply !== 'BOTH') {
            newParams.power_supply = newData.power_supply;
            setInputs({ ...inputs, power_supply: newData.power_supply });
          } else {
            const ps = PowerSupplyOptions[1].value;
            newParams.power_supply = ps;
            setInputs({ ...inputs, power_supply: ps });
          }
        }
        setActiveUserInputs(newParams);
      }
      setIsLoading(false);
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
      setIsLoading(false);
    }
  };

  const aggregateChange = async (aggregatedId: string) => {
    try {
      getAggregateData(aggregatedId);
      navigate(`/configure/${aggregatedId}${isExpertPage ? '/true' : ''}`, {
        replace: true,
      });
    } catch (error) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }
  };

  const updateField = (key: string, value: any) => {
    if (key === 'aggregate') {
      // new aggreagate chosen... recalc
      aggregateChange(value);
    }
    const newInputs = { ...inputs, [key]: value };
    setInputs({ ...newInputs });
  };

  // Track successful fetch for this page session to avoid duplicate calls
  const hasFetchedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    // Load aggregate details (without calculating) on mount/refresh
    const aggregateId = productId || activeAggregate?.id;
    if (!aggregateId) return;

    // Wait for auth to be ready before attempting fetch
    if (!isAuthenticated) return;
    if (!user) return; // user loaded async after isAuthenticated
    if (!isExpert) return; // isExpert loaded with user

    // Skip if already fetched for this aggregateId in this session
    if (hasFetchedRef.current === aggregateId) return;

    // Mark as fetched and load details only (no calculation)
    hasFetchedRef.current = aggregateId;
    loadAggregateDetails(aggregateId);
  }, [productId, activeAggregate?.id, isAuthenticated, user, isExpert]);

  const onSubmit = () => {
    setActiveUserInputs({ ...activeUserInputs, ...inputs });
    navigate('/addons');
  };

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isExpert, user]);

  React.useEffect(() => {
    if (isExpertPage && (!isAuthenticated || (user && !isExpert))) {
      navigate('/404', { replace: true });
    }
  }, [isExpertPage, isExpert, user]);

  return (
    <div className="product-page">
      <h2 className="product-page__title">
        {t(`productPage.title${isExpertPage ? 'Expert' : ''}`)}
      </h2>
      <div className="product-page__aggregate">
        <div className="product-page__aggregate__title">
          {t('productPage.aggregate')}:
        </div>
        <div>{activeAggregate?.name}</div>
      </div>
      <Form
        size="mini"
        className="product-page__inputs"
        onSubmit={() =>
          activeAggregate ? getAggregateData(activeAggregate.id) : null
        }
      >
        <div className="product-page__inputs__wrapper">
          {isExpertPage && (
            <AggregateExpertSelections updateField={updateField} />
          )}
          <AirflowInputs
            updateField={updateField}
            inputs={inputs}
            setInputs={setInputs}
          />
        </div>
        <Button primary type="submit">
          {t('productPage.calculateData')}
        </Button>
      </Form>
      <div className="product-page__calculations">
        {isLoading && (
          <Dimmer inverted active page>
            <Loader size="large">Loading</Loader>
          </Dimmer>
        )}
        <AggregateTableRow categories={categories} />
      </div>
      <div className="product-page__nav-btns footer fixed-footer">
        <Button
          onClick={() => {
            navigate('/products/');
          }}
        >
          {'<'} {t('navigation.back')}
        </Button>
        <Button primary onClick={() => onSubmit()}>
          {t('navigation.proceed')} {'>'}
        </Button>
      </div>
    </div>
  );
};

ConfigurePage.defaultProps = {
  categories: DefaultAggregateCategories,
  isExpertPage: false,
};

export default ConfigurePage;
